import { isNil } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { MBTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2, MBTextOverline } from '@shared/components/typography';
import { isDeletedEntity } from '@shared/data/entity/Entity';
import { RelatedItem } from '@shared/data/entity/fields/RelatedEntityField';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { useResponsiveFormContext } from '../../ResponsiveFormContext';
import { AddMoreButton } from '../AddMoreButton';
import { MAX_CARDS_TO_SHOW } from './constants';

interface Props {
  component: API.RelatedCardComponent;
  items: RelatedItem[];
  entities: API.WorkflowEntity[];
  openPicker: VoidFunction;
  updateItemQuantity: (id: string, quantity: number) => void;
  onItemPress?: (entity?: API.WorkflowEntity) => void;
  onItemRemove?: (entity?: API.WorkflowEntity) => void;
  openingEntity: API.WorkflowEntity | null;
  loading: boolean;
}

export function RelatedQuantityList({
  component,
  items,
  entities,
  openPicker,
  updateItemQuantity,
  onItemPress,
  onItemRemove,
  openingEntity,
  loading,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const isReadOnly = component.readonly ?? false;
  const [showAll, setShowAll] = useState(false);
  const count = entities.length;
  const renderShowMore = count > MAX_CARDS_TO_SHOW;
  const remainingCount = count - MAX_CARDS_TO_SHOW;
  const itemsToShow = showAll ? items : items.slice(0, MAX_CARDS_TO_SHOW);
  const defaultEntityName = i18n('workflowTemplate.defaultRelatedCard', {
    workflowName: component.label,
  });

  return (
    <View paddingTop={responsiveForms ? 1.25 : 0} paddingBottom={responsiveForms ? 0.5 : 0}>
      <ColumnHeaders />
      <View>
        {itemsToShow.map(item => (
          <RelatedQuantityItem
            key={item.id}
            isReadOnly={isReadOnly}
            onItemPress={onItemPress}
            onItemRemove={onItemRemove}
            openingEntity={openingEntity}
            item={item}
            entities={entities}
            defaultEntityName={defaultEntityName}
            openPicker={openPicker}
            updateItemQuantity={updateItemQuantity}
            loading={loading}
          />
        ))}
      </View>
      {!isReadOnly && !responsiveForms && (
        <AddMoreButton component={component} onPress={openPicker} />
      )}
      {renderShowMore && (
        <MBTouchableOpacity onPress={() => setShowAll(prev => !prev)}>
          <MBView style={styles.showMore}>
            <MBTextBody2>
              {!showAll && i18n('card.blocks.cardsInput.overflow', { count: remainingCount })}
              {showAll && i18n('card.list.showLess')}
            </MBTextBody2>
          </MBView>
        </MBTouchableOpacity>
      )}
    </View>
  );
}

function ColumnHeaders() {
  const colors = useContext(ColorThemeContext);
  return (
    <View row>
      <View style={{ width: grid(5) }}>
        <MBTextOverline align="center" color={colors.input.label}>
          {i18n('card.blocks.cardsInput.quantity.header.quantity')}
        </MBTextOverline>
      </View>
      <View paddingLeft={2}>
        <MBTextOverline color={colors.input.label}>
          {i18n('card.blocks.cardsInput.quantity.header.name')}
        </MBTextOverline>
      </View>
    </View>
  );
}

interface RelatedQuantityItemProps extends Omit<Props, 'component' | 'items' | 'onShowMore'> {
  item: RelatedItem;
  defaultEntityName: string;
  isReadOnly: boolean;
}

function RelatedQuantityItem({
  item,
  defaultEntityName,
  entities,
  updateItemQuantity,
  isReadOnly,
  onItemPress,
  onItemRemove,
  openingEntity,
  loading,
}: RelatedQuantityItemProps) {
  const [value, setValue] = useState(String(item.quantity ?? 1));
  const colors = useContext(ColorThemeContext);
  const entity = entities.find(e => e.id === item.id);
  const entityName = entity?.fields?.name;
  const isMissingName = isNil(entityName);
  const isDeleted = isDeletedEntity(entity);

  const updateValue = useCallback((v: string) => {
    if (v.match(/^\d*$/)) {
      setValue(v);
    }
  }, []);

  useEffect(() => {
    const number = parseValue(value);
    if (!isNaN(number)) {
      updateItemQuantity(item.id, number);
    }
  }, [item.id, updateItemQuantity, value]);

  const onBlur = useCallback(() => {
    const number = parseValue(value);
    if (isNaN(number)) {
      setValue('1');
    } else {
      setValue(String(number));
    }
  }, [value, setValue]);

  return (
    <MBView style={styles.cardRow}>
      <MBTextInput
        value={value}
        onChangeText={updateValue}
        onBlur={onBlur}
        size="normal"
        keyboardType="number-pad"
        noPadding
        style={[styles.input, { borderColor: colors.input.borderInactive }]}
        editable={!isReadOnly}
      />
      <Pill
        sections={[entityName ?? defaultEntityName]}
        italic={isDeleted || isMissingName}
        maxWidth={250}
        onPress={isDeleted ? undefined : () => onItemPress?.(entity)}
        onRemove={() => onItemRemove?.(entity)}
        loading={loading || (!isNil(entity?.id) && entity?.id === openingEntity?.id)}
      />
    </MBView>
  );
}

function parseValue(value: string): number {
  const cleanedValue = value.replace(/\D/g, '');
  return parseInt(cleanedValue, 10);
}

const styles = StyleSheet.create({
  input: {
    height: 'auto',
    flexShrink: 0,
    flexGrow: 0,
    paddingVertical: grid(0.25),
    width: grid(5),
    textAlign: 'center',
    borderRadius: grid(0.5),
    marginVertical: 4,
    marginRight: grid(0.5),
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginBottom: grid(0.5),
  },
  showMore: {
    marginLeft: grid(1),
    marginRight: grid(1),
  },
});
